// Pure single-year variance-table logic (FR-003/FR-004). NO node:fs here
// — this module is imported by the client VarianceTable AND the vitest
// smoke test, so it must stay free of server-only imports.

import type {
  BudgetVsActualArtefact,
  Classification,
  LineType,
} from "./types";

export type VarianceRow = {
  canonical_id: string;
  label: string;
  line_type: LineType;
  classification: Classification;
  classification_rationale: string;
  budget_thousands: number | null;
  actual_thousands: number | null;
  variance_absolute_thousands: number | null;
  variance_percent: number | null;
  budget_not_set: boolean;
  cross_link: string | null;
  budget_source_page: number | null;
  actual_source_page: number | null;
  source_pdf_url: string | null;
};

export type SortKey =
  | "label"
  | "budget"
  | "actual"
  | "variance_absolute"
  | "variance_percent";
export type SortDir = "asc" | "desc";
export type LineFilter = "all" | "revenue" | "expense";

/**
 * Single-year table rows for the most-recent reporting year (FR-003:
 * 100% of consolidated-statement lines present that year).
 */
export function buildVarianceRows(
  artefact: BudgetVsActualArtefact,
  pdfUrls: Record<number, string>,
  year: number = artefact.most_recent_year,
): VarianceRow[] {
  const rows: VarianceRow[] = [];
  for (const [cid, blk] of Object.entries(
    artefact.derived.variance_by_line_by_year,
  )) {
    const yr = blk.by_year[String(year)];
    if (!yr) continue; // line not present that year (taxonomy break)
    rows.push({
      canonical_id: cid,
      label: blk.current_label,
      line_type: blk.line_type,
      classification: blk.classification,
      classification_rationale: blk.classification_rationale,
      budget_thousands: yr.budget_thousands,
      actual_thousands: yr.actual_thousands,
      variance_absolute_thousands: yr.variance_absolute_thousands,
      variance_percent: yr.variance_percent,
      budget_not_set: yr.budget_not_set,
      cross_link: blk.cross_link,
      budget_source_page: yr.budget_source_page,
      actual_source_page: yr.actual_source_page,
      source_pdf_url: pdfUrls[year] ?? null,
    });
  }
  return rows;
}

export function filterRows(
  rows: VarianceRow[],
  filter: LineFilter,
): VarianceRow[] {
  if (filter === "all") return rows;
  if (filter === "revenue")
    return rows.filter(
      (r) =>
        r.line_type === "revenue" || r.line_type === "other_contribution",
    );
  return rows.filter((r) => r.line_type === "expense");
}

// nulls ("not budgeted" → no %/budget) sort last regardless of direction.
function cmpNullable(
  a: number | null,
  b: number | null,
  dir: SortDir,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return dir === "asc" ? a - b : b - a;
}

export function sortRows(
  rows: VarianceRow[],
  key: SortKey,
  dir: SortDir,
): VarianceRow[] {
  const out = [...rows];
  out.sort((x, y) => {
    if (key === "label")
      return dir === "asc"
        ? x.label.localeCompare(y.label)
        : y.label.localeCompare(x.label);
    const pick = (r: VarianceRow) =>
      key === "budget"
        ? r.budget_thousands
        : key === "actual"
          ? r.actual_thousands
          : key === "variance_absolute"
            ? r.variance_absolute_thousands
            : r.variance_percent;
    return cmpNullable(pick(x), pick(y), dir);
  });
  return out;
}

export type PerLineYear = {
  year: number;
  budget_thousands: number | null;
  actual_thousands: number | null;
  actual_source_page: number | null;
  source_pdf_url: string | null;
};

/**
 * Full multi-year budget+actual series per line (FR-010/FR-011/FR-012):
 * oldest→newest, only the years the line is actually observed (the 2022
 * taxonomy break truncates expense lines — the gap is surfaced by absence,
 * never back-filled).
 */
export function buildPerLineSeries(
  artefact: BudgetVsActualArtefact,
  pdfUrls: Record<number, string>,
): Record<string, PerLineYear[]> {
  const out: Record<string, PerLineYear[]> = {};
  for (const [cid, blk] of Object.entries(
    artefact.derived.variance_by_line_by_year,
  )) {
    out[cid] = Object.entries(blk.by_year)
      .map(([y, v]) => ({
        year: Number(y),
        budget_thousands: v.budget_thousands,
        actual_thousands: v.actual_thousands,
        actual_source_page: v.actual_source_page,
        source_pdf_url: pdfUrls[Number(y)] ?? null,
      }))
      .sort((a, b) => a.year - b.year);
  }
  return out;
}

/**
 * Canonical-ids of the top-N lines by |variance| in the given year
 * (SC-006: per-line drilldown for ≥ top-10 by absolute 2024 variance).
 * Never-budgeted lines (null variance) are excluded — no meaningful
 * variance to rank.
 */
export function topNByAbsVariance(
  rows: VarianceRow[],
  n: number,
): Set<string> {
  return new Set(
    [...rows]
      .filter((r) => r.variance_absolute_thousands !== null)
      .sort(
        (a, b) =>
          Math.abs(b.variance_absolute_thousands as number) -
          Math.abs(a.variance_absolute_thousands as number),
      )
      .slice(0, n)
      .map((r) => r.canonical_id),
  );
}

export type RowTotals = {
  budget_thousands: number;
  actual_thousands: number;
  variance_absolute_thousands: number;
  count: number;
};

/** Totals over a (filtered) row set; nulls excluded so a "not budgeted"
 * line never poisons the budget total. */
export function totalsFor(rows: VarianceRow[]): RowTotals {
  return rows.reduce<RowTotals>(
    (acc, r) => ({
      budget_thousands: acc.budget_thousands + (r.budget_thousands ?? 0),
      actual_thousands: acc.actual_thousands + (r.actual_thousands ?? 0),
      variance_absolute_thousands:
        acc.variance_absolute_thousands +
        (r.variance_absolute_thousands ?? 0),
      count: acc.count + 1,
    }),
    {
      budget_thousands: 0,
      actual_thousands: 0,
      variance_absolute_thousands: 0,
      count: 0,
    },
  );
}
